import React, { useState, useEffect } from 'react';
import { apiFetch } from '../../utils/api';
import { Calendar, Plus, Loader2, Phone, Mail, Users, MessageSquare, Clock, Trash2 } from 'lucide-react';

const ActivitiesManagement: React.FC = () => {
    const [activities, setActivities] = useState<any[]>([]);
    const [contacts, setContacts] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);

    const [newActivity, setNewActivity] = useState({
        contact_id: '', type: 'Call', subject: '', description: '', scheduled_at: ''
    });

    const fetchData = async () => {
        setLoading(true);
        try {
            const [actRes, conRes] = await Promise.all([
                apiFetch('/crm/activities'),
                apiFetch('/crm/contacts')
            ]);
            if (actRes.ok) setActivities(await actRes.json());
            if (conRes.ok) setContacts(await conRes.json());
        } catch (err) { console.error(err); } finally { setLoading(false); }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const res = await apiFetch('/crm/activities', {
                method: 'POST',
                body: JSON.stringify(newActivity)
            });
            if (res.ok) {
                setShowAddModal(false);
                setNewActivity({ contact_id: '', type: 'Call', subject: '', description: '', scheduled_at: '' });
                fetchData();
            }
        } catch (err) { console.error(err); }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this activity?')) return;
        try {
            const res = await apiFetch(`/crm/activities/${id}`, { method: 'DELETE' });
            if (res.ok) fetchData();
        } catch (err) { console.error(err); }
    };

    if (loading) return <div className="flex justify-center p-12"><Loader2 className="animate-spin text-blue-500" size={40} /></div>;

    const getTypeIcon = (type: string) => {
        switch (type) {
            case 'Call': return <Phone size={16} />;
            case 'Email': return <Mail size={16} />;
            case 'Meeting': return <Users size={16} />;
            default: return <MessageSquare size={16} />;
        }
    };

    return (
        <div className="space-y-8">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800">CRM Activities</h2>
                    <p className="text-gray-500">Track all calls, meetings, and communications</p>
                </div>
                <button
                    onClick={() => setShowAddModal(true)}
                    className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition font-bold shadow-lg shadow-blue-200"
                >
                    <Plus size={20} /> Log Activity
                </button>
            </div>

            <div className="grid grid-cols-1 gap-6">
                {activities.map((act, i) => (
                    <div key={i} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all flex gap-6 items-start group relative">
                        <button
                            onClick={() => handleDelete(act.id)}
                            className="absolute top-6 right-6 p-2 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                            <Trash2 size={18} />
                        </button>
                        <div className={`p-4 rounded-xl ${act.type === 'Call' ? 'bg-blue-50 text-blue-600' :
                            act.type === 'Email' ? 'bg-amber-50 text-amber-600' :
                                act.type === 'Meeting' ? 'bg-purple-50 text-purple-600' : 'bg-gray-50 text-gray-600'
                            }`}>
                            {getTypeIcon(act.type)}
                        </div>
                        <div className="flex-1">
                            <div className="flex justify-between items-start mb-2">
                                <div>
                                    <h3 className="font-bold text-gray-800 text-lg">{act.subject}</h3>
                                    <div className="flex items-center gap-2 text-sm text-gray-500 font-medium">
                                        <Clock size={14} />
                                        {new Date(act.scheduled_at || act.created_at).toLocaleString()}
                                        <span className="text-gray-300">|</span>
                                        <Users size={14} />
                                        {contacts.find(c => c.id === act.contact_id)?.name || 'Unknown Contact'}
                                    </div>
                                </div>
                                <span className={`mr-12 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${act.type === 'Call' ? 'bg-blue-100 text-blue-700' :
                                    act.type === 'Email' ? 'bg-amber-100 text-amber-700' :
                                        act.type === 'Meeting' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'
                                    }`}>
                                    {act.type}
                                </span>
                            </div>
                            <p className="text-gray-600 leading-relaxed">{act.description}</p>
                        </div>
                    </div>
                ))}

                {activities.length === 0 && (
                    <div className="flex flex-col items-center justify-center p-20 bg-gray-50 rounded-3xl border-2 border-dashed border-gray-200">
                        <Calendar size={64} className="text-gray-200 mb-4" />
                        <p className="text-gray-400 font-medium">No activities recorded yet</p>
                    </div>
                )}
            </div>

            {showAddModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-3xl p-8 max-w-xl w-full">
                        <h3 className="text-2xl font-bold mb-6">Log New Activity</h3>
                        <form onSubmit={handleCreate} className="space-y-4">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">Contact *</label>
                                <select
                                    className="w-full border p-3 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                                    value={newActivity.contact_id}
                                    onChange={e => setNewActivity({ ...newActivity, contact_id: e.target.value })}
                                    required
                                >
                                    <option value="">Select Contact</option>
                                    {contacts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Type *</label>
                                    <select
                                        className="w-full border p-3 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                                        value={newActivity.type}
                                        onChange={e => setNewActivity({ ...newActivity, type: e.target.value })}
                                        required
                                    >
                                        <option value="Call">Call</option>
                                        <option value="Email">Email</option>
                                        <option value="Meeting">Meeting</option>
                                        <option value="Note">Note</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Schedule Date</label>
                                    <input
                                        type="datetime-local"
                                        className="w-full border p-3 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                                        value={newActivity.scheduled_at}
                                        onChange={e => setNewActivity({ ...newActivity, scheduled_at: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">Subject *</label>
                                <input
                                    className="w-full border p-3 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                                    placeholder="Brief title of the activity"
                                    value={newActivity.subject}
                                    onChange={e => setNewActivity({ ...newActivity, subject: e.target.value })}
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">Description</label>
                                <textarea
                                    className="w-full border p-3 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                                    rows={3}
                                    value={newActivity.description}
                                    onChange={e => setNewActivity({ ...newActivity, description: e.target.value })}
                                    placeholder="Enter details..."
                                />
                            </div>
                            <div className="flex justify-end gap-4 pt-4">
                                <button type="button" onClick={() => setShowAddModal(false)} className="px-6 py-2 text-gray-500 font-bold">Cancel</button>
                                <button type="submit" className="px-8 py-2 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 shadow-md shadow-blue-100">Log Activity</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ActivitiesManagement;
